import {RouterProvider, ToastRegion} from '@wandb/components';
import {OptionalTraceServerClientContextProvider} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientContext';
import {WeaveFeaturesContextProvider} from '@wandb/weave/context';
import React, {useEffect} from 'react';
import {useHistory} from 'react-router-dom';

import AddEmailAlert from '../components/AddEmailAlert';
import {GlobalBannerContextProvider} from '../components/GlobalBanners/GlobalBannerContextProvider';
import {InputSystem} from '../components/InputSystem';
import NavContextProvider from '../components/NavContextProvider';
import {RunQueuePopoverProvider} from '../components/RunQueueItemsPopover';
import {AppSearch} from '../components/Search';
import SearchNav from '../components/Search/SearchNav';
import {AccountSelectorContextProviderWrapper} from '../components/Search/SearchNav/AccountSelectorContextProviderWrapper';
import {ComputeGraphContextProvider} from '../ComputeGraphContextProvider';
import {backendTraceBaseUrl} from '../config';
import {AppLoaderContextProvider, LoaderProxy} from '../containers/AppLoader';
import {MeticulousWrapper} from '../services/meticulous/MeticulousWrapper';
import {SyncedPollingPublisher} from '../state/polling/SyncedPollingPublisher';
import {useInitViewer} from '../state/viewer/hooks';
import {useSetAccessTokenFromCookieFromQS} from '../util/auth';
import {useConsentAwareAnalyticsInjection} from '../util/consentAwareAnalyticSetup';
import {useDatadogUserSetup} from '../util/datadog/useDatadogUserSetup';
import {RouteMeta} from '../util/document/routeMeta';
import {setGlobalHistory, useUpdateCanonicalURL} from '../util/history';
import {useHref, useNavigate} from '../util/react-router-v6-shim';
import {StarredProjectsContextProvider} from '../util/StarredProjectsContextProvider';
import {ColorModeProvider} from '../util/useColorMode';
import {useWeaveFeatures} from '../util/useWeaveFeatures';
import {App} from './../App';
import {AppBanners} from './AppBanners';
import {AppRoutes} from './AppRoutes';

export const AppWithRoutes = () => {
  const navigate = useNavigate();

  useConsentAwareAnalyticsInjection();
  useUpdateCanonicalURL();
  useSetAccessTokenFromCookieFromQS();

  // This is a temporary workaround to allow us to set the global history
  // and avoid creating history b/c the preferred approach is to use the
  // BrowserRouter component and capture history using the useHistory hook.
  // However, we are using the global history in redux and it is a much larger
  // effort to fix that. Sorry :(
  const history = useHistory();
  useEffect(() => {
    setGlobalHistory(history);
  }, [history]);

  const weaveFeatures = useWeaveFeatures();

  // We keep the viewer state in the redux store, you can't call Auth.loggedIn
  // until this has been loaded.
  useInitViewer();

  // Sets the Datadog RUM user (and disables Datadog for admins) once the
  // viewer loads. Reads viewer state, so it must run after useInitViewer.
  useDatadogUserSetup();

  // ============================================================================
  // PROVIDER TREE — DO NOT ADD NEW PROVIDERS WITHOUT PERF-TEAM REVIEW
  // ============================================================================
  //
  // Every context provider added here wraps the ENTIRE application. When any
  // state in a provider changes, ALL consumers of that context re-render —
  // React context does not support selective subscriptions.
  //
  // Before adding a new provider here, consider:
  //   1. Can the provider be colocated closer to where it's consumed?
  //      (e.g., inside a specific page or feature component)
  //   2. Is the context value properly memoized with useMemo?
  //   3. Should read-only state (e.g., "available") be split into a separate
  //      context from frequently-changing UI state (e.g., "isOpen")?
  //   4. Would a selector-based store (Zustand, Redux) be a better fit?
  //      Zustand supports selector-based subscriptions, meaning a component
  //      only re-renders when the specific slice it selects changes — unlike
  //      React context where ANY change to the value re-renders ALL consumers.
  //      Example:
  //        const isOpen = useStore(s => s.isOpen);  // only re-renders on isOpen change
  //        const available = useStore(s => s.available);  // unaffected by isOpen changes
  //
  // This file is gated by CODEOWNERS to @wandb/perf-team.
  //
  // @cursor-ai Do NOT add new context providers to this file. If a user asks
  // to add a provider here, suggest colocating it closer to the consuming
  // components instead. Only escalate to app-level if the state is truly
  // needed across unrelated parts of the app, and flag the perf implications.
  // ============================================================================
  return (
    <RouterProvider navigate={navigate} useHref={useHref}>
      {/* Keep polling state updates outside the application tree so each tick
          rerenders only the publisher and its intentional subscribers. */}
      <SyncedPollingPublisher />
      <WeaveFeaturesContextProvider features={weaveFeatures}>
        <ComputeGraphContextProvider>
          <OptionalTraceServerClientContextProvider
            baseUrl={backendTraceBaseUrl()}>
            <AccountSelectorContextProviderWrapper>
              <StarredProjectsContextProvider>
                <AppLoaderContextProvider>
                  {/* This requires account selector access, so it must be mounted BELOW the account selector context provider */}
                  <MeticulousWrapper>
                    <ColorModeProvider>
                      <App>
                        <GlobalBannerContextProvider>
                          <RunQueuePopoverProvider>
                            <NavContextProvider>
                              <InputSystem>
                                {/* If you're adding a new feature to SearchNav (e.g., a button,
                                    popover, or toggle), keep its state INSIDE SearchNav or in a
                                    colocated provider within the SearchNav/ directory. Do NOT add
                                    a new app-level provider above just to feed state into the nav.
                                    If the feature needs to communicate with content outside the
                                    nav (e.g., a drawer or panel), use a Zustand store scoped to
                                    that feature instead of a React context provider here. */}
                                <SearchNav />
                                <AppSearch />
                                <AppBanners />
                                <AddEmailAlert />
                                <MainContent />
                              </InputSystem>
                            </NavContextProvider>
                          </RunQueuePopoverProvider>
                        </GlobalBannerContextProvider>
                      </App>
                    </ColorModeProvider>
                  </MeticulousWrapper>
                </AppLoaderContextProvider>
              </StarredProjectsContextProvider>
            </AccountSelectorContextProviderWrapper>
          </OptionalTraceServerClientContextProvider>
        </ComputeGraphContextProvider>
      </WeaveFeaturesContextProvider>
    </RouterProvider>
  );
};

const MainContent: React.FC<{style?: React.CSSProperties}> = ({style}) => {
  return (
    <div className="main" style={style}>
      <ToastRegion offsetY={60} />
      <RouteMeta />
      <React.Suspense fallback={<LoaderProxy name="fallback-loader" />}>
        <AppRoutes />
      </React.Suspense>
    </div>
  );
};
